import type { AuthStep } from '@jusbrasil-web/shared-auth-flows'
import {
  AUTH_STEP_TYPE,
  StepNavigationProvider,
  useAuthNavigation,
} from '@jusbrasil-web/shared-auth-flows'
import type { BifrostModalTriggerOptions } from '@jusbrasil-web/shared-bifrost'
import { useFeatureFlags } from '@jusbrasil-web/shared-feature-flags'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useCallback, useEffect, useRef, useState } from 'react'

import type { SignupFormData } from '../../api/types'
import { AccountLimitReached } from '../../components/account-limit-reached'
import { AuthFooter } from '../../components/auth-footer'
import { CompleteDataForm } from '../../components/complete-data-form'
import { EmailOtpStep } from '../../components/email-otp-step'
import { EmailPasswordReset } from '../../components/email-password-reset'
import { EmailStep } from '../../components/email-step'
import { EmailValidationAccount } from '../../components/email-validation-account'
import { EntityNoticeEntry } from '../../components/entity-notice-entry'
import { EntityNoticeOtpStep } from '../../components/entity-notice-otp-step'
import { EntityNoticeTerminal } from '../../components/entity-notice-terminal'
import { ForgotPasswordStep } from '../../components/forgot-password-step'
import { GoogleStep } from '../../components/google-step'
import { LoginCard } from '../../components/login-card'
import { PasswordStep } from '../../components/password-step'
import { SignupCpfForm } from '../../components/signup-cpf-form'
import { SignupModalHeader, useSignupModalCtaTracker } from '../../components/signup-modal-header'
import { MODAL_EVENTS } from '../../constants'
import type {
  EntityNoticePageKind,
  EntityNoticeVariant,
} from '../../experiments/entity-notice-fakedoor'
import {
  emitEntityNoticeStepEvent,
  ENTITY_NOTICE_FINALIZE_SUBTITLE,
  ENTITY_NOTICE_FINALIZE_TITLE,
  isEntityNoticePageKind,
  isEntityNoticeVariant,
} from '../../experiments/entity-notice-fakedoor'
import { resolveSignupModalCopy } from '../../experiments/signup-modal/copy'
import type { SignupModalFlags, SignupModalVariant } from '../../experiments/signup-modal/flag'
import { isSignupModalVariant, SIGNUP_MODAL_FLAG_KEY } from '../../experiments/signup-modal/flag'
import {
  type SignupModalHeaderCopy,
  buildSignupModalHeaderCopy,
} from '../../experiments/signup-modal/highlight-copy'
import type { SignupModalSegment } from '../../experiments/signup-modal/segment'
import { useEntityNoticeFinalizeSignup } from '../../hooks/useEntityNoticeFinalizeSignup'
import { useSignUpOrSignIn } from '../../hooks/useSignUpOrSignIn'
import { isAllowedParentLocation } from '../../utils/allowed-origin'
import { saveEntityNoticeCompleteData } from '../../utils/entity-notice-complete-data'
import { type AuthPageEventData, buildUserAccountEventData } from '../../utils/event-data'
import { isValidRedirectUrl } from '../../utils/redirect-url'
import styles from './login-iframe.module.scss'

const IFRAME_Z_INDEX = 9001

const ENTITY_NOTICE_STEP_TYPES_WITH_INLINE_FOOTER: string[] = [
  AUTH_STEP_TYPE.ENTITY_NOTICE_ENTRY,
  AUTH_STEP_TYPE.ENTITY_NOTICE_FINALIZE,
]

interface IframeOptions {
  nextUrl?: string
  appId?: string
  email?: string
  featureCode?: string
  fromComponent?: string
  onCloseCallback?: () => void
  entityNoticeVariant?: EntityNoticeVariant
  entityNoticeEntityId?: string
  entityNoticePageKind?: EntityNoticePageKind
}

export interface LoginIframePageProps {
  signupModalForcedVariant?: SignupModalVariant | null
  signupModalSegment?: SignupModalSegment
  signupModalEntityName?: string | null
  signupModalEntityType?: 'PF' | 'CNPJ' | null
  signupModalExposureEligible?: boolean
  signupModalInExperiment?: boolean
  entityNoticeVariant?: EntityNoticeVariant | null
  entityNoticeEntityId?: string | null
  entityNoticePageKind?: EntityNoticePageKind | null
}

function flattenEventData(raw?: BifrostModalTriggerOptions): IframeOptions {
  const result: IframeOptions = {}
  if (typeof raw?.nextUrl === 'string' && isValidRedirectUrl(raw.nextUrl))
    result.nextUrl = raw.nextUrl
  if (raw?.onCloseCallback !== undefined) result.onCloseCallback = raw.onCloseCallback
  if (raw?.eventData?.app_id !== undefined) result.appId = raw.eventData.app_id
  if (raw?.eventData?.email !== undefined) result.email = raw.eventData.email
  if (raw?.eventData?.feature_code !== undefined) result.featureCode = raw.eventData.feature_code
  if (raw?.eventData?.from_component !== undefined)
    result.fromComponent = raw.eventData.from_component
  const urlVariant = raw?.urlParams?.entityNoticeVariant
  if (isEntityNoticeVariant(urlVariant)) result.entityNoticeVariant = urlVariant
  const urlEntityId = raw?.urlParams?.entityNoticeEntityId
  if (typeof urlEntityId === 'string' && urlEntityId) result.entityNoticeEntityId = urlEntityId
  const urlPageKind = raw?.urlParams?.entityNoticePageKind
  if (isEntityNoticePageKind(urlPageKind)) result.entityNoticePageKind = urlPageKind
  return result
}

function openFrame() {
  const frameEl = window.frameElement as HTMLElement | null
  if (!frameEl) return
  frameEl.removeAttribute('hidden')
  frameEl.style.cssText = `height:100%;width:100%;left:0;top:0;position:fixed;border:none;z-index:${IFRAME_Z_INDEX}`
  const scrollEl = window.parent.document.scrollingElement as HTMLElement | null
  if (scrollEl) scrollEl.style.overflow = 'hidden'
}

function closeFrame() {
  const frameEl = window.frameElement as HTMLElement | null
  if (!frameEl) return
  frameEl.setAttribute('hidden', '')
  frameEl.removeAttribute('style')
  const scrollEl = window.parent.document.scrollingElement as HTMLElement | null
  if (scrollEl) scrollEl.style.overflow = ''
}

export function LoginIframePage({
  signupModalForcedVariant = null,
  signupModalSegment,
  signupModalEntityName = null,
  signupModalEntityType = null,
  signupModalExposureEligible = false,
  signupModalInExperiment = false,
  entityNoticeVariant = null,
  entityNoticeEntityId = null,
  entityNoticePageKind = null,
}: LoginIframePageProps = {}) {
  const { getFeatureValue } = useFeatureFlags<SignupModalFlags>()

  const [isOpen, setIsOpen] = useState(false)
  const isOpenRef = useRef(false)
  const [openCount, setOpenCount] = useState(0)
  const [options, setOptions] = useState<IframeOptions>({})
  const optionsRef = useRef(options)
  optionsRef.current = options
  const initialOptionsRef = useRef<IframeOptions>({})
  const stepEmailRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    document.body.classList.add(styles.body)
    return () => {
      document.body.classList.remove(styles.body)
    }
  }, [])

  const handleClose = useCallback(() => {
    window.parent.Bifrost?.close()
  }, [])

  const handleAuthSuccess = useCallback(() => {
    window.parent.location.href = optionsRef.current.nextUrl ?? window.parent.location.href
  }, [])

  const handleStepEmailChange = useCallback((email: string | undefined) => {
    stepEmailRef.current = email
  }, [])

  useEffect(() => {
    if (!isAllowedParentLocation()) return
    if (window.parent.Bifrost) return // singleton guard

    window.parent.Bifrost = {
      open: (newOptions) => {
        isOpenRef.current = true
        stepEmailRef.current = undefined
        const flattened = {
          ...initialOptionsRef.current,
          ...flattenEventData(newOptions as BifrostModalTriggerOptions),
        }
        if (!flattened.nextUrl) {
          flattened.nextUrl = window.parent.location.href
        }
        optionsRef.current = flattened
        setOptions(flattened)
        openFrame()
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'custom_component_rendered',
          ga4_component_name: 'lock_authentication',
          ga4_trigger_feature: flattened.featureCode || null,
        })
        setIsOpen(true)
        setOpenCount((c) => c + 1)
      },
      close: () => {
        if (!isOpenRef.current) return
        isOpenRef.current = false
        const { appId, featureCode, fromComponent } = optionsRef.current
        sendMetrics(
          MODAL_EVENTS.CLOSED,
          null,
          buildUserAccountEventData(
            { appId, featureCode, fromComponent, email: stepEmailRef.current },
            { type: 'LogInModal' }
          )
        )
        setIsOpen(false)
        closeFrame()
        optionsRef.current.onCloseCallback?.()
      },
      mergeInitialOptions: (newOptions) => {
        const flattened = flattenEventData(newOptions)
        initialOptionsRef.current = { ...initialOptionsRef.current, ...flattened }
        setOptions((prev) => ({ ...prev, ...flattened }))
      },
    }

    return () => {
      closeFrame()
      delete window.parent.Bifrost
    }
  }, [])

  if (!isOpen) return null

  // Anti-pattern look, deliberate: this iframe never reloads on click, so
  // runtime must win over the SSR value or entity-notice never activates.
  const effectiveEntityNoticeVariant = options.entityNoticeVariant ?? entityNoticeVariant
  const effectiveEntityNoticeEntityId = options.entityNoticeEntityId ?? entityNoticeEntityId
  const effectiveEntityNoticePageKind = options.entityNoticePageKind ?? entityNoticePageKind

  const entityNoticeActive = isEntityNoticeVariant(effectiveEntityNoticeVariant)

  const flagVariant =
    !signupModalForcedVariant && signupModalExposureEligible
      ? getFeatureValue(SIGNUP_MODAL_FLAG_KEY)
      : undefined
  const signupModalVariant: SignupModalVariant | null =
    signupModalForcedVariant ?? (isSignupModalVariant(flagVariant) ? flagVariant : null)

  const signupModalActive = signupModalVariant !== null && signupModalSegment !== undefined
  const signupModalHeader: SignupModalHeaderCopy | undefined = signupModalActive
    ? buildSignupModalHeaderCopy(
        resolveSignupModalCopy({
          variant: signupModalVariant,
          segment: signupModalSegment,
          entityName: signupModalEntityName || undefined,
          entityType: signupModalEntityType || undefined,
        })
      )
    : undefined

  return (
    <StepNavigationProvider
      key={openCount}
      {...(entityNoticeActive && {
        initialStep: { type: AUTH_STEP_TYPE.ENTITY_NOTICE_ENTRY },
      })}
    >
      {(step) => (
        <div className={styles.modal}>
          <button
            type="button"
            className={styles.overlay}
            aria-label="Fechar modal"
            onClick={handleClose}
          />
          <div className={styles.content} data-testid="modal-content">
            <div className={styles.contentInner} data-testid="login-iframe-content-inner">
              {signupModalActive && (
                <SignupModalHeader
                  variant={signupModalVariant}
                  segment={signupModalSegment}
                  entityName={signupModalEntityName || undefined}
                  entityType={signupModalEntityType || undefined}
                  appId={options.appId || undefined}
                  featureCode={options.featureCode || undefined}
                  fromComponent={options.fromComponent || undefined}
                  inExperiment={signupModalInExperiment}
                />
              )}
              <LoginIframeFlow
                step={step}
                options={options}
                signupModalHeader={signupModalHeader}
                signupModalVariant={signupModalVariant}
                signupModalSegment={signupModalSegment}
                signupModalInExperiment={signupModalInExperiment}
                entityNoticeVariant={effectiveEntityNoticeVariant}
                entityNoticeEntityId={effectiveEntityNoticeEntityId}
                entityNoticePageKind={effectiveEntityNoticePageKind}
                entityNoticeEntityName={signupModalEntityName}
                onClose={handleClose}
                onAuthSuccess={handleAuthSuccess}
                onStepEmailChange={handleStepEmailChange}
              />
            </div>
          </div>
          {!ENTITY_NOTICE_STEP_TYPES_WITH_INLINE_FOOTER.includes(step.type) && (
            <AuthFooter
              className={styles.iframeFooter}
              showAgreementText={step.type === AUTH_STEP_TYPE.EMAIL}
            />
          )}
        </div>
      )}
    </StepNavigationProvider>
  )
}

interface LoginIframeFlowProps {
  step: AuthStep
  options: IframeOptions
  signupModalHeader?: SignupModalHeaderCopy
  signupModalVariant?: SignupModalVariant | null
  signupModalSegment?: SignupModalSegment
  signupModalInExperiment?: boolean
  entityNoticeVariant?: EntityNoticeVariant | null
  entityNoticeEntityId?: string | null
  entityNoticePageKind?: EntityNoticePageKind | null
  entityNoticeEntityName?: string | null
  onClose: () => void
  onAuthSuccess: () => void
  onStepEmailChange: (email: string | undefined) => void
}

function LoginIframeFlow({
  step,
  options,
  signupModalHeader,
  signupModalVariant = null,
  signupModalSegment = 'unknown',
  entityNoticeVariant = null,
  entityNoticeEntityId = null,
  entityNoticePageKind = null,
  entityNoticeEntityName = null,
  signupModalInExperiment = false,
  onClose,
  onAuthSuccess,
  onStepEmailChange,
}: LoginIframeFlowProps) {
  const navigate = useAuthNavigation()
  const { appId, email, featureCode, fromComponent, nextUrl } = options

  const trackSignupModalCta = useSignupModalCtaTracker({
    variant: signupModalSegment === 'unknown' ? null : signupModalVariant,
    segment: signupModalSegment,
    appId,
    featureCode,
    fromComponent,
    inExperiment: signupModalInExperiment,
  })

  const modalSeenEventDataRef = useRef(
    buildUserAccountEventData({ appId, email, featureCode, fromComponent }, { type: 'LogInModal' })
  )

  useEffect(() => {
    onStepEmailChange('email' in step ? step.email : undefined)
  }, [step, onStepEmailChange])

  useEffect(() => {
    sendMetrics(MODAL_EVENTS.SEEN, null, modalSeenEventDataRef.current)
  }, [])

  const handleAccountExists = useCallback(
    (email: string) => {
      navigate({ type: AUTH_STEP_TYPE.PASSWORD, email })
    },
    [navigate]
  )

  const handleGoogleLoginRequired = useCallback(
    (email: string) => {
      navigate({ type: AUTH_STEP_TYPE.GOOGLE_ONLY, email })
    },
    [navigate]
  )

  const handleSignupEmailSent = useCallback(
    (email: string) => {
      navigate({ type: AUTH_STEP_TYPE.SIGNUP_EMAIL_SENT, email })
    },
    [navigate]
  )

  const handleOtpRequired = useCallback(
    (email: string) => {
      navigate({ type: AUTH_STEP_TYPE.SIGNUP_OTP, email })
    },
    [navigate]
  )

  const handleChangeOtpEmail = useCallback(() => {
    navigate({ type: AUTH_STEP_TYPE.EMAIL })
  }, [navigate])

  const handleRateLimited = useCallback(() => {
    navigate({ type: AUTH_STEP_TYPE.RATE_LIMITED })
  }, [navigate])

  const handleResendRecovery = useCallback(() => {
    navigate({ type: AUTH_STEP_TYPE.RECOVERY })
  }, [navigate])

  const {
    isLoading: entityNoticeEntryLoading,
    error: entityNoticeEntryError,
    handleEmailSubmit: submitEntityNoticeEmail,
  } = useSignUpOrSignIn({
    allowsOtp: true,
    onAccountExists: handleAccountExists,
    onOtpRequired: useCallback(
      (otpEmail: string) => navigate({ type: AUTH_STEP_TYPE.ENTITY_NOTICE_OTP, email: otpEmail }),
      [navigate]
    ),
    onSignupEmailSent: handleSignupEmailSent,
  })

  const handleEntityNoticeEntrySubmit = useCallback(
    (submittedEmail: string) => {
      emitEntityNoticeStepEvent({
        variant: entityNoticeVariant as EntityNoticeVariant,
        step: 'entry',
        action: 'submit',
        pageKind: entityNoticePageKind,
        contact: submittedEmail,
      })
      if (entityNoticeVariant === 'variantC') {
        navigate({ type: AUTH_STEP_TYPE.ENTITY_NOTICE_OTP, email: submittedEmail })
        return
      }
      submitEntityNoticeEmail(submittedEmail)
    },
    [entityNoticeVariant, entityNoticePageKind, submitEntityNoticeEmail, navigate]
  )

  const handleEntityNoticeOtpVerified = useCallback(
    (otpEmail: string) => (token: string) => {
      emitEntityNoticeStepEvent({
        variant: entityNoticeVariant as EntityNoticeVariant,
        step: 'otp',
        action: 'submit',
        pageKind: entityNoticePageKind,
      })
      navigate({ type: AUTH_STEP_TYPE.ENTITY_NOTICE_COMPLETE_DATA, email: otpEmail, token })
    },
    [entityNoticeVariant, entityNoticePageKind, navigate]
  )

  const handleEntityNoticeCompleteDataSubmit = useCallback(
    (completeDataEmail: string, token: string) => async (data: SignupFormData) => {
      saveEntityNoticeCompleteData(data)
      await emitEntityNoticeStepEvent({
        variant: entityNoticeVariant as EntityNoticeVariant,
        step: 'complete-data',
        action: 'submit',
        pageKind: entityNoticePageKind,
      })
      navigate({ type: AUTH_STEP_TYPE.ENTITY_NOTICE_FINALIZE, email: completeDataEmail, token })
    },
    [entityNoticeVariant, entityNoticePageKind, navigate]
  )

  const entityNoticeFinalizeStepEmail =
    step.type === AUTH_STEP_TYPE.ENTITY_NOTICE_FINALIZE ? step.email : ''
  const entityNoticeFinalizeStepToken =
    step.type === AUTH_STEP_TYPE.ENTITY_NOTICE_FINALIZE ? step.token : ''
  const { error: entityNoticeFinalizeError, submitCpf: submitEntityNoticeCpf } =
    useEntityNoticeFinalizeSignup({
      token: entityNoticeFinalizeStepToken,
      onSuccess: useCallback(() => {
        emitEntityNoticeStepEvent({
          variant: entityNoticeVariant as EntityNoticeVariant,
          step: 'finalize',
          action: 'submit',
          pageKind: entityNoticePageKind,
        })
        navigate({
          type: AUTH_STEP_TYPE.ENTITY_NOTICE_TERMINAL,
          email: entityNoticeFinalizeStepEmail,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [entityNoticeVariant, entityNoticePageKind, entityNoticeFinalizeStepEmail, navigate]),
    })

  const handleEntityNoticeSeeMyLawsuits = useCallback(() => {
    onAuthSuccess()
  }, [onAuthSuccess])

  const commonProps = {
    appId,
    featureCode,
    fromComponent,
    nextUrl,
    onCloseClick: onClose,
  }

  const eventData: AuthPageEventData = { appId, featureCode, fromComponent }

  switch (step.type) {
    case AUTH_STEP_TYPE.EMAIL:
      return (
        <EmailStep
          {...commonProps}
          allowsOtp
          signupModalHeader={signupModalHeader}
          onAccountExists={handleAccountExists}
          onGoogleLoginRequired={handleGoogleLoginRequired}
          onSignupEmailSent={handleSignupEmailSent}
          onOtpRequired={handleOtpRequired}
          onRateLimited={handleRateLimited}
          onCtaResolved={trackSignupModalCta}
        />
      )
    case AUTH_STEP_TYPE.PASSWORD:
      return (
        <PasswordStep
          email={step.email}
          {...commonProps}
          signupModalHeader={signupModalHeader}
          onAuthSuccess={onAuthSuccess}
        />
      )
    case AUTH_STEP_TYPE.RECOVERY:
      return <ForgotPasswordStep email={step.email} onCloseClick={onClose} />
    case AUTH_STEP_TYPE.RECOVERY_SENT:
      return <EmailPasswordReset onCloseClick={onClose} onResendClick={handleResendRecovery} />
    case AUTH_STEP_TYPE.SIGNUP_EMAIL_SENT:
      return <EmailValidationAccount email={step.email} onCloseClick={onClose} />
    case AUTH_STEP_TYPE.SIGNUP_OTP:
      return (
        <EmailOtpStep
          email={step.email}
          nextUrl={nextUrl}
          onChangeEmail={handleChangeOtpEmail}
          onCloseClick={onClose}
        />
      )
    case AUTH_STEP_TYPE.GOOGLE_ONLY:
      return (
        <GoogleStep
          email={step.email}
          eventData={eventData}
          nextUrl={nextUrl}
          onCloseClick={onClose}
        />
      )
    case AUTH_STEP_TYPE.RATE_LIMITED:
      return <AccountLimitReached eventData={eventData} onCloseClick={onClose} />
    case AUTH_STEP_TYPE.ENTITY_NOTICE_ENTRY:
      return isEntityNoticeVariant(entityNoticeVariant) ? (
        <EntityNoticeEntry
          variant={entityNoticeVariant}
          entityName={entityNoticeEntityName}
          onSubmit={handleEntityNoticeEntrySubmit}
          isLoading={entityNoticeEntryLoading}
          error={entityNoticeEntryError}
          onCloseClick={onClose}
          appId={appId}
          featureCode={featureCode}
          fromComponent={fromComponent}
          nextUrl={nextUrl}
        />
      ) : null
    case AUTH_STEP_TYPE.ENTITY_NOTICE_OTP:
      return entityNoticeVariant === 'variantC' ? (
        <EntityNoticeNotImplemented onCloseClick={onClose} />
      ) : (
        <EntityNoticeOtpStep
          email={step.email}
          onVerified={handleEntityNoticeOtpVerified(step.email)}
          onCloseClick={onClose}
        />
      )
    case AUTH_STEP_TYPE.ENTITY_NOTICE_COMPLETE_DATA:
      return (
        <CompleteDataForm
          email={step.email}
          centerHeader
          onSubmit={handleEntityNoticeCompleteDataSubmit(step.email, step.token)}
        />
      )
    case AUTH_STEP_TYPE.ENTITY_NOTICE_FINALIZE:
      return (
        <LoginCard
          header={{
            align: 'center',
            title: ENTITY_NOTICE_FINALIZE_TITLE,
            subtitle: { content: ENTITY_NOTICE_FINALIZE_SUBTITLE },
            ...(entityNoticeFinalizeError && {
              errorMessage: { title: entityNoticeFinalizeError },
            }),
          }}
          onCloseClick={onClose}
        >
          <SignupCpfForm
            signUpByEmailToken={step.token}
            email={step.email}
            hidePhoneFields
            onSubmit={({ cpf, birthdate, phone, whatsAppOptIn }) =>
              submitEntityNoticeCpf({ cpf, birthdate, phone, whatsAppOptIn })
            }
          />
        </LoginCard>
      )
    case AUTH_STEP_TYPE.ENTITY_NOTICE_TERMINAL:
      return (
        <EntityNoticeTerminal
          email={step.email}
          entityId={entityNoticeEntityId}
          variant={entityNoticeVariant}
          pageKind={entityNoticePageKind}
          onSeeMyLawsuits={handleEntityNoticeSeeMyLawsuits}
          onClose={onClose}
        />
      )
    default:
      return null
  }
}

/**
 * variantC's interim stop, in the OTP step's slot — QA2-8c replaces this
 * with cell C's real (lead-only, never-creates-an-account) terminal.
 */
function EntityNoticeNotImplemented({ onCloseClick }: { onCloseClick: () => void }) {
  return (
    <div className={styles.entityNoticeNotImplemented}>
      <p>Ainda não disponível para este fluxo.</p>
      <button type="button" onClick={onCloseClick}>
        Fechar
      </button>
    </div>
  )
}
